import type { DeadSessionReason } from './refreshFailure';

/**
 * Cierra una sesión que el SERVIDOR ha dado por muerta (spec 2026-10-01 § 5.3).
 *
 * Borra la sesión (y con ella la copia para reabrir sin red) y la caché de
 * Apollo, para que quien entre después no vea los datos del anterior.
 *
 * 🔴 NO toca nada que sea trabajo del operario: colas offline, borradores,
 * fotos ni tracks de GPS. Esa es la diferencia con «Cerrar sesión» manual
 * (`UserMenu/action.ts`), que lo vacía todo. Lo encolado va sellado con su
 * dueño (spec § 6) y se enviará cuando vuelva a entrar.
 */
export interface EndDeadSessionDeps {
  clearSession: () => void;
  clearApolloCache: () => Promise<unknown>;
  purgePersistedCache: () => Promise<unknown>;
  currentPath: () => string;
  navigate: (url: string) => void;
  report?: (reason: DeadSessionReason) => void;
}

export function createEndDeadSession(
  deps: EndDeadSessionDeps,
): (reason: DeadSessionReason) => Promise<void> {
  let running: Promise<void> | null = null;

  const endOnce = async (reason: DeadSessionReason): Promise<void> => {
    // El aviso es diagnóstico: si lanza, no puede impedir que la sesión se
    // borre y el operario llegue al login.
    try {
      deps.report?.(reason);
    } catch {
      // Sin aviso; el cierre sigue.
    }
    deps.clearSession();
    // Limpiar la caché puede fallar (una consulta en vuelo); eso no puede
    // impedir que el operario llegue al login.
    await Promise.allSettled([
      Promise.resolve().then(deps.clearApolloCache),
      Promise.resolve().then(deps.purgePersistedCache),
    ]);
    // Sin este guard, cada consulta que falle ya en el login relanzaría la
    // navegación y el operario no podría teclear.
    if (!deps.currentPath().startsWith('/login')) {
      deps.navigate(`/login?reason=${encodeURIComponent(reason)}`);
    }
  };

  return (reason) => {
    if (!running) {
      running = endOnce(reason).finally(() => {
        running = null;
      });
    }
    return running;
  };
}
